/**
 * Narrowing helpers for values caught in `catch` blocks and mutation
 * `onError` callbacks, which are `unknown` to TypeScript. They cover the
 * three shapes thrown in this codebase: `Error` instances, axios-style
 * errors (`error.response.status` / `error.response.data`) and plain
 * objects carrying `status` / `message` (the fetch-based clients).
 */
import { isRecord } from './typeGuards';

/** Minimal shape of an axios-style error, without importing axios. */
export interface ResponseLikeError {
  message?: unknown;
  status?: unknown;
  response?: {
    status?: unknown;
    data?: unknown;
  };
}

/** True when `error` is a non-null object (safe to index). */
export function isErrorLike(error: unknown): error is ResponseLikeError {
  return isRecord(error);
}

/**
 * HTTP status of an axios-style (`error.response.status`) or
 * fetch-style (`error.status`) error, or `undefined`.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (!isErrorLike(error)) return undefined;
  const status = error.response?.status ?? error.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * The `error.message` of whatever was thrown (Error instance, API error
 * object or plain string), or `fallback` when there is none.
 */
export function getErrorMessage(error: unknown, fallback = ''): string {
  if (typeof error === 'string') return error || fallback;
  if (error instanceof Error) return error.message || fallback;
  if (isErrorLike(error) && typeof error.message === 'string') {
    return error.message || fallback;
  }
  return fallback;
}

/**
 * Render a response-body field the way the old `err.response?.data?.key`
 * interpolation did: strings as-is, arrays joined with ', ' (a 422 often
 * carries `details: [...]`), objects as JSON, other primitives via String().
 */
function stringifyField(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map(item => (typeof item === 'string' ? item : JSON.stringify(item)))
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * A field of an axios-style error body (`error.response.data[key]`) as a
 * string, e.g. `getResponseErrorField(err, 'error')`, or `undefined` when
 * the error has no body or the field is missing.
 */
export function getResponseErrorField(
  error: unknown,
  key: string
): string | undefined {
  if (!isErrorLike(error)) return undefined;
  const data = error.response?.data;
  if (!isRecord(data)) return undefined;
  return stringifyField(data[key]);
}

export function isForbiddenError(error: unknown): boolean {
  if (!error) return false;

  // Axios / fetch Response error shape
  const status = getErrorStatus(error);
  if (status === 401 || status === 403) return true;

  // Error message containing HTTP status
  if (error instanceof Error) {
    return /\b(401|403)\b/.test(error.message);
  }

  // Plain string error message containing HTTP status
  if (typeof error === 'string') {
    return /\b(401|403)\b/.test(error);
  }

  return false;
}
